import React, { useMemo } from 'react'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import { makeStyles, Dialog } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { PresentationImbedPreview } from '../../PresentationPreview'
import { QuoteContextProvider, useQuote } from '../../../context'
import { ExitPreviewButton } from '../../../common'
import type { PresentationOptionV2, QuoteDocument } from 'paintscout'
import { getQuoteNextPresentation } from '@paintscout/util/builder'

export interface QuotePreviewDialogProps extends DialogProps {
  onConfirm?: (event: React.MouseEvent<HTMLElement>) => void
  givenQuote?: QuoteDocument
  givenPresentation?: PresentationOptionV2
  /**
   * Dim the preview because another dialog is stacked on top of it. Both surfaces are white and
   * full width, so without this they read as one page (ENG-1481). The caller decides: it knows
   * whether this preview is still the top of the dialog stack.
   */
  isCovered?: boolean
  /**
   * Passed through to `getQuoteNextPresentation` when no `givenPresentation` with pages is
   * supplied. Defaults to `true` — this dialog is the estimator's own preview, not the
   * customer-facing frozen-at-send copy, so it should reflect builder edits made since the quote
   * was last saved (ENG-1403). A future caller that specifically wants the quote's frozen
   * snapshot (e.g. a "view as sent" surface) can pass `false`.
   */
  current?: boolean
  /**
   * Raise a quote the preview changed — picking a Good/Better/Best package from inside it. The
   * caller holds the copy that gets accepted, so it has to hear about the choice (ENG-1624).
   */
  onQuoteChange?: (quote: QuoteDocument) => void
}

const useStyles = makeStyles(
  (_theme: Theme) => {
    return {
      dialogPaper: {
        overflow: 'hidden'
      },
      paperScrollPaper: {
        display: 'block'
      },
      presPreview: {
        height: '100vh'
      },
      // Inside the dialog, which is its own stacking context: it covers the preview, and the dialog
      // stacked above still paints over it.
      stackedScrim: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000
      }
    }
  },
  { name: 'QuotePreviewDialog' }
)

function QuotePreviewDialog(props: QuotePreviewDialogProps) {
  const classes = useStyles(props)
  const {
    current = true,
    givenPresentation,
    givenQuote,
    isCovered,
    onClose,
    onConfirm,
    onQuoteChange,
    ...baseDialogProps
  } = props
  const { options } = useClientOptions()

  // have two quote options so it works w/ and w/o context
  const { quote: usedQuote, estimator, wisetackPromo } = useQuote()
  const quote = givenQuote ? givenQuote : usedQuote
  // A presentation without pages crashes the preview renderer, so only trust
  // givenPresentation when it has pages; otherwise derive one from the quote.
  const presentation =
    (givenPresentation?.pages?.length ?? 0) > 0
      ? givenPresentation
      : getQuoteNextPresentation({ options, quote, current })

  // This provider replaces the surrounding editor's context for everything in the preview, so
  // what the preview still needs has to be carried across: the Wisetack promo the editor
  // fetched, and the fact that this renders the customer-facing document (without
  // isPresentation the Wisetack apply button stays live instead of inert — ENG-833).
  // Memoized: QuoteContextProvider keys its value memo on this object's identity.
  const contextValue = useMemo(
    () =>
      onQuoteChange
        ? {
            wisetackPromo,
            isPresentation: true,
            updateQuote: ({ quote: updated }: { quote: QuoteDocument }) => onQuoteChange(updated)
          }
        : { wisetackPromo, isPresentation: true },
    [wisetackPromo, onQuoteChange]
  )

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper, paperScrollPaper: classes.paperScrollPaper }}
      forceFullScreen={true}
      scroll="body"
      fullWidth={true}
      PaperProps={{
        id: 'quote-preview-dialog-paper'
      }}
      onClose={onClose}
      {...baseDialogProps}
    >
      {isCovered && <div className={classes.stackedScrim} aria-hidden />}
      <QuoteContextProvider
        quote={{ ...quote, presentationTemplate: presentation }}
        estimator={estimator}
        contextValue={contextValue}
      >
        <PresentationImbedPreview
          border={'none'}
          loadingStyle={'cv'}
          classes={{ root: classes.presPreview }}
          handleAcceptClick={onConfirm ?? null}
          isPreviewAccept={true}
          onDismissDialog={onClose}
          presentation={presentation}
          usePresentationFromProp={true}
          onQuoteChange={onQuoteChange}
          quoteId={quote?._id}
        />
      </QuoteContextProvider>
      {/*
       * Fixed bottom-left "Exit Preview" pill — the single exit affordance both presentation
       * versions use, so the control does not move between them (ENG-1505).
       *
       * It renders here, in the dialog, rather than inside `CustomerViewContents`: that tree is
       * portalled into the preview iframe, and iOS ignores an iframe's CSS height and grows the
       * frame to its content instead. The iframe's own viewport then equals the whole
       * presentation, so `position: fixed` inside it pinned the pill to the bottom of the
       * content — reachable only after scrolling the entire presentation, and clipped by the
       * browser chrome once you got there (ENG-1715). Outside the iframe it pins to the real
       * viewport. `stackedScrim` still covers it: the scrim's higher z-index wins.
       */}
      <ExitPreviewButton onClick={(ev) => onClose?.(ev, 'escapeKeyDown')} />
    </Dialog>
  )
}

export default QuotePreviewDialog
